import React, { useEffect, useState, useRef } from "react";
import AgoraRTC, { IAgoraRTCClient, ILocalAudioTrack, ILocalVideoTrack, IAgoraRTCRemoteUser } from "agora-rtc-sdk-ng";
import { toast } from "react-toastify";
import { Box, Typography, IconButton, Paper, Button, CircularProgress, Avatar } from "@mui/material";
import { Mic, MicOff, CallEnd, Person, Videocam, VideocamOff } from "@mui/icons-material";

interface AgoraCallProps {
  appId: string;
  channelName: string;
  token: string;
  uid: number;
  participants?: { id: number; name: string }[];
  onClose: () => void;
  onEndCall?: (duration: string) => void;
  isVideoCall?: boolean;
}

const AgoraCall: React.FC<AgoraCallProps> = ({ appId, channelName, token, uid, participants = [], onClose, onEndCall, isVideoCall }) => {
  const [client, setClient] = useState<IAgoraRTCClient | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<ILocalAudioTrack | null>(null);
  const [localVideoTrack, setLocalVideoTrack] = useState<ILocalVideoTrack | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [status, setStatus] = useState<"joining" | "connected" | "error">("joining");
  const [errorMessage, setErrorMessage] = useState("");
  const [startTime] = useState(Date.now());
  const localVideoRef = useRef<HTMLDivElement>(null);
  const [isVideoActive, setIsVideoActive] = useState(isVideoCall || false);

  useEffect(() => {
    if (isVideoCall || remoteUsers.some(u => u.hasVideo)) {
      setIsVideoActive(true);
    }
  }, [isVideoCall, remoteUsers]);

  useEffect(() => {
    let agoraClient: IAgoraRTCClient | null = null;
    let audioTrack: ILocalAudioTrack | null = null;
    let videoTrack: ILocalVideoTrack | null = null;

    const init = async () => {
      try {
        agoraClient = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        setClient(agoraClient);

        agoraClient.on("user-published", async (user, mediaType) => {
          if (agoraClient) {
            await agoraClient.subscribe(user, mediaType);
            if (mediaType === "audio") {
              user.audioTrack?.play();
              // Remote party audio reported as too quiet to hear clearly, especially in group
              // calls. Range is 0-1000 (100 = original level); matches the mobile app's
              // AgoraCallModal.tsx boost level. (There's no earpiece/speaker distinction to
              // default on the web — playback always goes to whatever output device the
              // browser/OS has selected, which is already the "speaker" in WhatsApp's sense.)
              user.audioTrack?.setVolume(400);
            }
            setRemoteUsers([...agoraClient.remoteUsers]);
          }
        });

        agoraClient.on("user-unpublished", (user, mediaType) => {
          if (agoraClient) {
            setRemoteUsers([...agoraClient.remoteUsers]);
          }
        });

        agoraClient.on("user-mute-updated", (user: IAgoraRTCRemoteUser, isMuted: boolean) => {
          console.log(`[AgoraCall Web] Remote user ${user.uid} mute state updated: ${isMuted}`);
          if (agoraClient) {
            setRemoteUsers([...agoraClient.remoteUsers]);
          }
        });

        agoraClient.on("user-left", (user) => {
          if (agoraClient) {
            setRemoteUsers([...agoraClient.remoteUsers]);
          }
        });

        setStatus("joining");
        console.log("Agora Initialization Info:");
        console.log("  - AppID:", appId);
        console.log("  - Channel:", channelName);
        console.log("  - Token:", token ? (token.substring(0, 10) + "...") : "NULL");
        console.log("  - UID:", uid);
        
        if (!appId || appId === "API_KEY") {
           alert("CRITICAL ERROR: AppID is missing or invalid! Value: " + appId);
        }
        
        await agoraClient.join(appId, channelName, token || null, uid || 0);
        
        if (isVideoCall) {
          try {
            const [aTrack, vTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
            audioTrack = aTrack;
            videoTrack = vTrack;
            setLocalAudioTrack(audioTrack);
            setLocalVideoTrack(videoTrack);
            setIsVideoMuted(false);
            await agoraClient.publish([audioTrack, videoTrack]);
          } catch (videoErr: any) {
            console.warn("Combined track creation failed, falling back to separate creation:", videoErr);
            try {
              audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
              setLocalAudioTrack(audioTrack);
            } catch (aErr) {
              console.error("Audio track error:", aErr);
            }

            try {
              videoTrack = await AgoraRTC.createCameraVideoTrack();
              setLocalVideoTrack(videoTrack);
              setIsVideoMuted(false);
            } catch (vErr) {
              console.error("Camera track error:", vErr);
              setIsVideoMuted(true);
            }

            const tracksToPub = [audioTrack, videoTrack].filter(Boolean) as (ILocalAudioTrack | ILocalVideoTrack)[];
            if (tracksToPub.length > 0) {
              await agoraClient.publish(tracksToPub);
            }
          }
        } else {
          audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
          setLocalAudioTrack(audioTrack);
          setIsVideoMuted(true);
          await agoraClient.publish([audioTrack]);
        }
        
        setStatus("connected");
      } catch (error: any) {
        console.error("Agora join failed", error);
        setStatus("error");
        setErrorMessage(error.message || "Failed to join call. Check permissions or Agora token.");
      }
    };

    init();

    return () => {
      if (audioTrack) {
        audioTrack.stop();
        audioTrack.close();
      }
      if (videoTrack) {
        videoTrack.stop();
        videoTrack.close();
      }
      if (agoraClient) {
        agoraClient.leave().catch(console.error);
      }
    };
  }, [appId, channelName, token, uid, isVideoCall]);

  useEffect(() => {
    if (localVideoTrack && localVideoRef.current && status === "connected" && !isVideoMuted) {
      try {
        localVideoTrack.play(localVideoRef.current);
      } catch (e) {
        console.error("Error playing local video:", e);
      }
    }
  }, [localVideoTrack, status, isVideoMuted]);

  useEffect(() => {
     remoteUsers.forEach(user => {
       if (user.videoTrack && user.hasVideo) {
         try {
           user.videoTrack.play(`remote-video-${user.uid}`);
         } catch (e) {
           console.error("Error playing remote video:", e);
         }
       }
     });
  }, [remoteUsers]);

  const toggleMute = () => {
    if (localAudioTrack) {
      localAudioTrack.setMuted(!isMuted);
      setIsMuted(!isMuted);
    }
  };

  const toggleVideoMute = async () => {
    if (localVideoTrack) {
      const nextMuteState = !isVideoMuted;
      try {
        await localVideoTrack.setMuted(nextMuteState);
        setIsVideoMuted(nextMuteState);
      } catch (e: any) {
        console.warn("Failed to set video track mute state:", e);
      }
    } else if (client) {
      try {
        const vTrack = await AgoraRTC.createCameraVideoTrack();
        setLocalVideoTrack(vTrack);
        setIsVideoMuted(false);
        await client.publish([vTrack]);
      } catch (err: any) {
        console.error("Failed to start camera video track:", err);
        toast.error("Could not access camera: " + (err.message || "Permission denied"));
      }
    }
  };

  const handleEndCall = async () => {
    const durationMs = Date.now() - startTime;
    const mins = Math.floor(durationMs / 60000);
    const secs = Math.floor((durationMs % 60000) / 1000);
    const durationStr = `${mins}:${secs < 10 ? '0' : ''}${secs}`;

    // WhatsApp-style Group Call logic:
    // Only terminate the entire call session in the DB if you are the last person in the room.
    // Otherwise, just leave the Agora channel silently and let others continue talking.
    if (onEndCall && remoteUsers.length === 0) {
        onEndCall(durationStr);
    }

    if (localAudioTrack) {
      localAudioTrack.stop();
      localAudioTrack.close();
    }
    if (localVideoTrack) {
      localVideoTrack.stop();
      localVideoTrack.close();
    }
    if (client) {
      await client.leave();
    }
    onClose();
  };

  // isVideoActive state handles this persistently above

  return (
    <Paper
      elevation={3}
      sx={
        isVideoActive ? {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000,
          backgroundColor: "#111", // Dark background for video
          display: "flex",
          flexDirection: "column",
          color: "white",
          margin: 0,
          padding: 0,
          boxSizing: "border-box"
        } : {
          position: "fixed",
          bottom: 20,
          right: 20,
          width: 300,
          p: 2,
          borderRadius: 4,
          zIndex: 10000,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          textAlign: "center",
        }
      }
    >
      {!isVideoActive && (
        <Typography variant="h6" gutterBottom>
          Audio Call
        </Typography>
      )}

      {status === "error" ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error" variant="body2" mb={2}>{errorMessage}</Typography>
          <Button variant="outlined" size="small" onClick={onClose}>Close</Button>
        </Box>
      ) : (
        <Box sx={{ position: "relative", flexGrow: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          {status === "joining" && (
            <Box sx={{ 
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0, 
              bgcolor: "rgba(255,255,255,0.7)", zIndex: 2,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              borderRadius: 2
            }}>
              <CircularProgress size={30} sx={{ mb: 1 }} />
              <Typography variant="caption">Connecting...</Typography>
            </Box>
          )}
          
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            flexWrap: "wrap", 
            gap: isVideoActive ? 2 : 1,
            flexGrow: isVideoActive ? 1 : 0,
            p: isVideoActive ? 2 : 0,
            pb: isVideoActive ? "100px" : 0, // Padding bottom for the absolute controls
            overflowY: "auto",
            overflowX: "hidden",
            width: "100%"
          }}>
            {isVideoActive ? (
              <>
                 {/* Local Video */}
                 <Box sx={{ position: "relative", flex: "1 1 calc(50% - 16px)", minWidth: 240, maxWidth: 600, maxHeight: "40vh", aspectRatio: "16/9", bgcolor: 'black', borderRadius: 4, overflow: 'hidden', boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
                    <Box ref={localVideoRef} sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 1 }} />
                    {isVideoMuted && (
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#1e293b", zIndex: 2 }}>
                        <VideocamOff sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
                        <Typography variant="body2" sx={{ color: "#ef4444", fontWeight: 700 }}>Camera Off</Typography>
                      </Box>
                    )}
                    <Typography variant="caption" sx={{ position: "absolute", bottom: 12, left: 12, bgcolor: "rgba(0,0,0,0.85)", color: "#fff", fontWeight: 600, px: 1.5, py: 0.5, borderRadius: 1, zIndex: 5, boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
                       You{isVideoMuted ? " (Cam Off)" : ""}
                    </Typography>
                 </Box>

                 {/* Remote Videos */}
                 {remoteUsers.map(user => {
                   const userName = participants.find(p => Number(p.id) === Number(user.uid))?.name || `Participant ${user.uid}`;
                   const isRemoteCamOff = !user.hasVideo || !user.videoTrack;
                   const isRemoteAudioMuted = !user.hasAudio;
                   return (
                     <Box key={user.uid} sx={{ position: "relative", flex: "1 1 calc(50% - 16px)", minWidth: 240, maxWidth: 600, maxHeight: "40vh", aspectRatio: "16/9", bgcolor: 'black', borderRadius: 4, overflow: 'hidden', boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
                       <Box id={`remote-video-${user.uid}`} sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 1 }} />
                       {isRemoteCamOff && (
                         <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#1e293b", zIndex: 2 }}>
                           <VideocamOff sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
                           <Typography variant="body2" sx={{ color: "#ef4444", fontWeight: 700 }}>Camera Off</Typography>
                         </Box>
                       )}
                       <Typography variant="caption" sx={{ position: "absolute", bottom: 12, left: 12, bgcolor: "rgba(0,0,0,0.85)", color: "#fff", fontWeight: 600, px: 1.5, py: 0.5, borderRadius: 1, zIndex: 5, boxShadow: "0 2px 8px rgba(0,0,0,0.3)", display: "flex", alignItems: "center", gap: 0.5 }}>
                          {userName}{isRemoteCamOff ? " (Cam Off)" : ""}
                          {isRemoteAudioMuted && <MicOff sx={{ fontSize: 14, color: "#ef4444", ml: 0.5 }} />}
                       </Typography>
                     </Box>
                   );
                 })}
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%', py: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
                  {/* Local User Chip */}
                  <Paper elevation={1} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 50,
                    bgcolor: isMuted ? '#fee2e2' : '#dcfce7',
                    border: '1px solid',
                    borderColor: isMuted ? '#f87171' : '#4ade80'
                  }}>
                    <Avatar sx={{ width: 30, height: 30, bgcolor: '#2563eb', fontSize: 14, fontWeight: 'bold' }}>
                      Y
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      You
                    </Typography>
                    {isMuted ? <MicOff sx={{ fontSize: 16, color: '#ef4444' }} /> : <Mic sx={{ fontSize: 16, color: '#16a34a' }} />}
                  </Paper>

                  {/* Remote Joined Users */}
                  {remoteUsers.map(user => {
                    const match = participants.find(p => Number(p.id) === Number(user.uid));
                    const name = match?.name || `User ${user.uid}`;
                    const isRemoteAudioMuted = !user.hasAudio;

                    return (
                      <Paper key={user.uid} elevation={1} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 50,
                        bgcolor: isRemoteAudioMuted ? '#fee2e2' : '#dcfce7',
                        border: '1px solid',
                        borderColor: isRemoteAudioMuted ? '#f87171' : '#4ade80'
                      }}>
                        <Avatar sx={{ width: 30, height: 30, bgcolor: isRemoteAudioMuted ? '#ef4444' : '#16a34a', fontSize: 14, fontWeight: 'bold' }}>
                          {name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {name}
                        </Typography>
                        {isRemoteAudioMuted ? (
                          <MicOff sx={{ fontSize: 16, color: '#ef4444' }} />
                        ) : (
                          <Mic sx={{ fontSize: 16, color: '#16a34a' }} />
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 600 }}>
            {remoteUsers.length > 0
              ? (remoteUsers.length === 1
                  ? `Connected with ${participants.find(p => Number(p.id) === Number(remoteUsers[0].uid))?.name || 'Participant'}`
                  : `Connected (${remoteUsers.length + 1} Participants In Call)`)
              : "Waiting for other party to join..."}
          </Typography>

          <Box sx={isVideoActive ? { 
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex", 
            justifyContent: "center", 
            gap: 3, 
            p: 3,
            pb: 4,
            bgcolor: "rgba(0,0,0,0.6)",
            zIndex: 10
          } : {
            display: "flex", 
            justifyContent: "center", 
            gap: 2 
          }}>
            <IconButton
              onClick={toggleMute}
              size={isVideoActive ? "large" : "medium"}
              sx={{
                backgroundColor: isMuted ? "error.main" : (isVideoActive ? "rgba(255,255,255,0.2)" : "grey.200"),
                color: isMuted ? "white" : (isVideoActive ? "white" : "inherit"),
                "&:hover": { backgroundColor: isMuted ? "error.dark" : (isVideoActive ? "rgba(255,255,255,0.3)" : "grey.300") },
              }}
            >
              {isMuted ? <MicOff /> : <Mic />}
            </IconButton>
            
            {isVideoCall && (
              <IconButton
                onClick={toggleVideoMute}
                size={isVideoActive ? "large" : "medium"}
                sx={{
                  backgroundColor: isVideoMuted ? "error.main" : (isVideoActive ? "rgba(255,255,255,0.2)" : "grey.200"),
                  color: isVideoMuted ? "white" : (isVideoActive ? "white" : "inherit"),
                  "&:hover": { backgroundColor: isVideoMuted ? "error.dark" : (isVideoActive ? "rgba(255,255,255,0.3)" : "grey.300") },
                }}
              >
                {isVideoMuted ? <VideocamOff /> : <Videocam />}
              </IconButton>
            )}

            <IconButton
              onClick={handleEndCall}
              size={isVideoActive ? "large" : "medium"}
              sx={{
                backgroundColor: "error.main",
                color: "white",
                "&:hover": { backgroundColor: "error.dark" },
              }}
            >
              <CallEnd />
            </IconButton>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default AgoraCall;
